import { type LocationContent } from "@/components/location-page";
import { FEES, FIRM, PENINSULA_ADDRESS, PENINSULA_OFFICE } from "@/lib/firm";

// The dedicated /conveyancing/$suburb pages own the "conveyancing <suburb>" search.
// These regional pages lead on wills, estates and elder law so the two do not compete.
const meta = (name: string) => ({
  metaTitle: `Wills, Estates & Elder Law Solicitors ${name} | ${FIRM.name}`,
  description: `Wills, enduring powers of attorney, probate and elder law advice for ${name}, from our Scarborough office. Fixed fees, solicitor-led. Call ${FIRM.phone}.`,
});

const MICHAEL_NOTE = {
  t: "A lawyer who knows the area",
  d: "Michael Klein lives in Redcliffe and was managing partner of GKS Law for many years. He still acts for long-standing Moreton Bay clients and takes referrals from agents across the peninsula and the northern corridor.",
};

const OFFICE_NOTE = {
  t: `Our peninsula office — ${PENINSULA_OFFICE.street}, ${PENINSULA_OFFICE.suburb}`,
  d: `We meet Moreton Bay clients at ${PENINSULA_ADDRESS} by appointment, so signing a will, an enduring power of attorney or a contract does not mean a drive to the Sunshine Coast.`,
};

const REMOTE_NOTE = {
  t: "You never need to come to us",
  d: "Queensland conveyancing runs electronically through PEXA. Contracts are signed digitally, ID is verified by video or through a local agent, and settlement happens online — so distance makes no difference to your file.",
};

const COMMON_FAQS = [
  {
    q: "Do you have an office in the Moreton Bay area?",
    a: `Yes. Our peninsula office is at ${PENINSULA_ADDRESS}, seen by appointment, alongside our Sunshine Coast office at ${FIRM.street}, ${FIRM.suburb}. Most Moreton Bay matters are run electronically, but you are welcome to sit down with us at Scarborough.`,
  },
  {
    q: "What are your fixed fees?",
    a: `Residential sales are ${FEES.sale} inclusive of GST and residential purchases are ${FEES.purchase} inclusive of GST. Searches, registration and government fees are additional and we set them out in writing before you commit.`,
  },
  {
    q: "Can you prepare my will and enduring power of attorney at the same time?",
    a: "Yes, and it is usually the sensible time to do it. Buying, selling or downsizing changes what your estate holds, so we review your will, enduring power of attorney and advance health directive alongside the conveyance.",
  },
];

function loc(
  slug: string,
  name: string,
  title: string,
  lead: string,
  paragraphs: string[],
  localNotes: { t: string; d: string }[],
  suburbs: string[],
  faqs: { q: string; a: string }[],
): LocationContent {
  return {
    slug,
    name,
    title,
    ...meta(name),
    lead,
    paragraphs,
    localNotes: [...localNotes, OFFICE_NOTE, MICHAEL_NOTE, REMOTE_NOTE],
    suburbs,
    officeAddress: PENINSULA_ADDRESS,
    faqs: [...faqs, ...COMMON_FAQS],
  };
}

export const MORETON_BAY_LOCATIONS: LocationContent[] = [
  loc(
    "redcliffe",
    "Redcliffe",
    "Wills, estates and elder law in Redcliffe",
    "Redcliffe is where our Legal Practice Director lives. From beachfront units on Marine Parade to post-war homes on the ridge, we act on fixed fees for buyers, sellers, downsizers and executors across the peninsula.",
    [
      "Redcliffe has two very different property markets sitting side by side: older detached homes on generous blocks, and a fast-growing stock of unit and townhouse developments along the waterfront and around the Kippa-Ring rail corridor. The contract issues are not the same, and the searches we recommend differ accordingly.",
      "For units, the body corporate records are the heart of the due diligence — sinking fund adequacy, building defect history, remediation levies and any by-laws on letting or pets. For older houses, we look harder at building approvals for extensions, sheds and decks, and at whether earlier work was ever certified.",
      "A large part of our Redcliffe work is estate related: probate and letters of administration for long-time residents, transmission of the family home to beneficiaries, and updating wills and enduring powers of attorney when a parent moves into aged care.",
    ],
    [
      {
        t: "Unit and body corporate due diligence",
        d: "We read the disclosure statement, body corporate records and minutes for special levies, defect claims and remediation projects before the cooling-off period ends.",
      },
      {
        t: "Unapproved building work",
        d: "Decks, carports and enclosed patios added over the decades are often uncertified. We check council records and deal with it in the contract rather than after settlement.",
      },
      {
        t: "Storm tide and flood overlays",
        d: "Low-lying and near-foreshore parts of the peninsula sit in storm tide overlays, which affect insurance and rebuild approvals.",
      },
    ],
    ["Scarborough", "Woody Point", "Margate", "Clontarf", "Kippa-Ring", "Rothwell"],
    [
      {
        q: "Do you handle Redcliffe deceased estates as well as conveyancing?",
        a: "Yes. We apply for probate or letters of administration, transmit the property to the executor or beneficiaries, and handle the sale of the family home where the estate needs it — all in the one file.",
      },
    ],
  ),
  loc(
    "scarborough",
    "Scarborough",
    "Wills, estates and elder law in Scarborough",
    "Scarborough's waterfront homes, marina units and retirement living options all carry different contract terms. We act on fixed fees and tell you what you are actually buying before the cooling-off period ends.",
    [
      "Scarborough combines premium waterfront and elevated homes with a strong retirement and downsizer market. Buyers here are often selling a larger family home elsewhere on the peninsula, which means two settlements to coordinate and a funding gap to identify early.",
      "Retirement village and over-50s living contracts are common in this pocket. A right to reside is not freehold — deferred management fees, refurbishment obligations and the resale timeline determine what your family eventually gets back, and those terms are far easier to question before signing.",
      "For waterfront and near-foreshore property we check erosion prone area and storm tide overlays, any revetment wall or seawall responsibility, and whether jetty or pontoon structures are properly approved and included in the sale.",
    ],
    [
      {
        t: "Retirement village contracts",
        d: "We explain the exit fees, refurbishment costs and buy-back timing in writing, and make sure the required disclosure documents were given before you signed.",
      },
      {
        t: "Waterfront structures",
        d: "Pontoons, jetties and seawalls need approvals and may sit on tidal land under permit. We confirm what transfers with the property.",
      },
      {
        t: "Sale and purchase timing",
        d: "Downsizers usually need both matters running together. We align settlement dates and flag bridging requirements early.",
      },
    ],
    ["Redcliffe", "Newport", "Woody Point", "Margate", "Kippa-Ring"],
    [
      {
        q: "Should a lawyer review my retirement village contract before I sign?",
        a: "Yes. The exit costs and resale rules in a village residence contract are the single biggest financial term in the document, and they are much easier to understand — and sometimes negotiate — before signing than during the cooling-off period.",
      },
    ],
  ),
  loc(
    "margate",
    "Margate",
    "Margate conveyancing, wills and elder law",
    "Margate is one of the peninsula's busiest turnover markets — first homes, investment units and knock-down-rebuild sites. We act on fixed fees, with the searches matched to what you are actually buying.",
    [
      "Margate attracts first home buyers and investors, so we see a high volume of finance and building-and-pest conditional contracts. Missing a condition date is the most common and most costly mistake in Queensland conveyancing, and we diarise every deadline and chase your broker before it lapses.",
      "First home buyers should get their transfer duty position confirmed before signing. Concessions depend on price, on occupying the property within the required time, and on not leasing it out too early. We calculate the duty and the concession up front so the figure at settlement is not a surprise.",
      "For older Margate blocks being bought for redevelopment, we check the zoning, minimum lot size and any easements or sewer alignments that would restrict a future build.",
    ],
    [
      {
        t: "First home buyer duty concessions",
        d: "Eligibility depends on price, occupation and how soon you lease. We confirm your position before you sign, not after.",
      },
      {
        t: "Condition dates",
        d: "Finance and building-and-pest dates in the REIQ contract are strict. We track them and give you written notice as each approaches.",
      },
      {
        t: "Redevelopment potential",
        d: "Zoning, minimum lot size, easements and sewer alignments decide whether a subdivision or rebuild is realistic.",
      },
    ],
    ["Woody Point", "Redcliffe", "Clontarf", "Kippa-Ring"],
    [
      {
        q: "How quickly can you review a contract before I sign?",
        a: "Usually the same day. Send it through with the agent's contact details and we will come back with any changes needed, the duty figure and an estimate of your total settlement costs.",
      },
    ],
  ),
  loc(
    "north-lakes",
    "North Lakes",
    "North Lakes conveyancing, wills and elder law",
    "North Lakes is covenant country — master-planned estates, community title schemes and house-and-land contracts. We read the fine print so it does not surprise you after settlement.",
    [
      "Almost every North Lakes property sits inside a master-planned community with covenants, community management statements or both. These control fencing, colours, parking, letting and even landscaping timelines. They are enforceable, and they pass to you on settlement.",
      "House-and-land and off-the-plan contracts here are not the standard REIQ residential contract. Sunset dates, staged titling, developer variation rights and progress payment schedules all need review, and the finance approval usually needs to remain valid far longer than in a normal purchase.",
      "North Lakes also has a large population of young families. This is exactly the group most likely to have no will at all — and with a mortgage, superannuation, life insurance and young children, an intestacy is the worst possible outcome. We prepare wills, enduring powers of attorney and guardianship provisions as a fixed-fee package.",
    ],
    [
      {
        t: "Covenants and community management statements",
        d: "We identify what the scheme actually restricts — and the levies attached — before you commit.",
      },
      {
        t: "Off-the-plan and sunset dates",
        d: "Staged titling means your settlement date can move. We explain your rights if the sunset date is reached.",
      },
      {
        t: "Wills for young families",
        d: "Guardianship of children, superannuation death benefit nominations and life insurance all need to be aligned with the will.",
      },
    ],
    ["Mango Hill", "Rothwell", "Griffin", "Murrumba Downs", "Deception Bay"],
    [
      {
        q: "Do I really need a will if I only own a mortgaged house?",
        a: "Yes. Superannuation and life insurance often make an estate far larger than people expect, and without a will Queensland's intestacy rules decide who receives what — and who raises your children. A simple will removes that uncertainty.",
      },
    ],
  ),
  loc(
    "caboolture",
    "Caboolture",
    "Caboolture conveyancing, wills and elder law",
    "Caboolture, Caboolture South and the surrounding acreage. We act on fixed fees for buyers, sellers, executors and families planning ahead.",
    [
      "Caboolture covers everything from compact estate housing to older town blocks and rural-residential land toward the hinterland. Each needs a different search strategy, and we tell you which searches actually matter for your property rather than ordering a standard bundle.",
      "Flood history is a genuine issue in parts of Caboolture. A flood search, and where relevant a flood level certificate, should be obtained before the cooling-off period ends — insurers and lenders both price on it.",
      "Our estates practice is busy here: probate and letters of administration, transferring the family home, executor advice and, increasingly, contested estate claims where a family member has been left out or provided for inadequately.",
    ],
    [
      {
        t: "Flood searches",
        d: "Caboolture River flooding affects specific pockets. We obtain the council information and explain what it means for insurance and resale.",
      },
      {
        t: "Rural-residential land",
        d: "Water, sewerage, access easements, shed approvals and overlays all need checking on larger blocks.",
      },
      {
        t: "Deceased estates",
        d: "We handle probate, letters of administration, property transmission and estate sales, and advise executors on their obligations.",
      },
    ],
    ["Morayfield", "Burpengary", "Elimbah", "Wamuran", "Woodford"],
    [
      {
        q: "How long does probate take in Queensland?",
        a: "After the required advertising and notice period, a straightforward application is usually granted within a few months, though the Supreme Court's workload varies. Administering and distributing the estate then takes longer, and executors should generally not distribute before six months from the grant.",
      },
    ],
  ),
  loc(
    "kallangur",
    "Kallangur",
    "Kallangur conveyancing, wills and elder law",
    "Kallangur is one of the busiest first-home-buyer markets in Moreton Bay. We act on fixed fees and explain the contract in plain English before the cooling-off period runs out.",
    [
      "Kallangur draws a high proportion of first home buyers, which means finance conditions, building and pest outcomes and grant eligibility do most of the work in a typical file. Getting the dates right at the front end avoids almost every problem that follows.",
      "We check the first home owner grant and transfer duty concession position early, because the concession depends on how you intend to occupy the property and can be clawed back if that changes.",
      "The suburb also has a large volume of investor stock and older homes with added structures, so building approval history and any existing tenancy terms are routine parts of our review.",
    ],
    [
      {
        t: "First home buyer duty concessions",
        d: "We confirm your transfer duty concession and grant eligibility before settlement, and explain the occupation conditions that keep it.",
      },
      {
        t: "Finance and building and pest dates",
        d: "Most failed contracts fail on dates. We diarise every condition and chase your broker and inspector so extensions are requested before the deadline, not after.",
      },
      {
        t: "Buying with an existing tenant",
        d: "If the property is tenanted, the lease continues after settlement. We check the term, rent and bond position.",
      },
    ],
    ["Petrie", "Murrumba Downs", "Dakabin", "Griffin", "North Lakes", "Mango Hill"],
    [
      {
        q: "Am I eligible for the Queensland first home transfer duty concession?",
        a: "It depends on the price, whether you are buying to live in the property, and how soon and how long you occupy it. We check your position before settlement and tell you what would trigger a clawback.",
      },
    ],
  ),
];


export const MORETON_BAY_SLUGS = MORETON_BAY_LOCATIONS.map((l) => l.slug);

export function getMoretonBayLocation(slug: string) {
  return MORETON_BAY_LOCATIONS.find((l) => l.slug === slug);
}
